/**
 * src/utils/deepLinkReturn.js
 *
 * Carries a deep link's #anchor across the production sign-in bounce
 * (ACEA-03b). The bounce sends pathname + search to login.tryggr.dk and comes
 * back with the auth hand-off in the fragment (#code=…, AUTH-B-008), so the
 * link's own fragment cannot travel with it. Without this, a version-pinned
 * link opened while signed out (/documents/doc-1?v=2.0#blk-0002) would return
 * without its anchor, fail the grammar, and show "This source can't be opened".
 *
 * sessionStorage is per tab and per origin, and survives the round trip to
 * another origin in the same tab. The stash holds no tenant data — only the
 * fragment of the URL the person themselves opened — and is consumed once,
 * only for the exact path + search it was saved for.
 */

const KEY = 'tryggr.deepLinkFragment';

/** Before the sign-in bounce: remember this URL's fragment, if any. */
export function rememberDeepLinkFragment(
  loc = typeof window !== 'undefined' ? window.location : null
) {
  try {
    if (!loc || !loc.hash || loc.hash.length < 2) return;
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ path: `${loc.pathname}${loc.search}`, hash: loc.hash })
    );
  } catch {
    /* storage unavailable: the link opens without its anchor (neutral state) */
  }
}

/**
 * After sign-in: the remembered fragment for exactly this path + search, or
 * null. Consumed whether or not it matches, so it can never attach to a later
 * page.
 */
export function takeDeepLinkFragment({ pathname, search } = {}) {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    const saved = JSON.parse(raw);
    if (!saved || saved.path !== `${pathname || ''}${search || ''}`) return null;
    return typeof saved.hash === 'string' && saved.hash.startsWith('#') ? saved.hash : null;
  } catch {
    return null;
  }
}
